(() => {
  if (!customElements.get('xenjoy-header')) {
    customElements.define('xenjoy-header', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const signal = this.controller.signal;
        const menu = this.querySelector('.xj-mobile-menu');
        if (!menu) return;
        const summary = menu.querySelector('summary');
        summary.setAttribute('aria-expanded', String(menu.open));
        menu.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', String(menu.open));
          summary.setAttribute('aria-label', menu.open ? this.dataset.menuClose : this.dataset.menuOpen);
        }, { signal });
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !menu.open) return;
          menu.open = false;
          summary.focus();
        }, { signal });
        document.addEventListener('click', (event) => {
          if (menu.open && !this.contains(event.target)) menu.open = false;
        }, { signal });
        document.addEventListener('focusin', (event) => {
          if (menu.open && !this.contains(event.target)) menu.open = false;
        }, { signal });
        menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => {
          menu.open = false;
        }, { signal }));
        // Dawn attaches its cart trigger once on initial load. This delegated
        // fallback also supports headers newly rendered by the theme editor.
        this.addEventListener('click', (event) => {
          const trigger = event.target.closest('#cart-icon-bubble');
          const drawer = document.querySelector('cart-drawer');
          if (!trigger || !drawer || event.defaultPrevented) return;
          event.preventDefault();
          drawer.open(trigger);
        }, { signal });
      }
      disconnectedCallback() { this.controller?.abort(); }
    });
  }

  if (!customElements.get('xenjoy-product')) {
    customElements.define('xenjoy-product', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const signal = this.controller.signal;
        try { this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]'); }
        catch { this.variants = []; }
        this.addEventListener('click', (event) => {
          const thumbnail = event.target.closest('[data-media-target]');
          if (thumbnail) this.selectMedia(thumbnail.dataset.mediaTarget);
        }, { signal });
        this.querySelector('[data-variant-select]')?.addEventListener('change', (event) => {
          this.selectVariant(event.target.value);
        }, { signal });
        const form = this.querySelector('form');
        const initialVariant = this.variants.find((variant) => String(variant.id) === String(form?.querySelector('[name=id]')?.value));
        if (initialVariant?.mediaId) this.selectMedia(initialVariant.mediaId);
        form?.addEventListener('submit', (event) => {
          if (!form.checkValidity()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            form.reportValidity();
          }
        }, { capture: true, signal });
        const sticky = this.querySelector('.xj-product__sticky');
        const mainButton = this.querySelector('.xj-product__add');
        const stickyButton = this.querySelector('[data-sticky-submit]');
        if (stickyButton && mainButton) {
          this.buttonObserver?.disconnect();
          this.buttonObserver = new MutationObserver(() => {
            stickyButton.disabled = mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true';
          });
          this.buttonObserver.observe(mainButton, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
        }
        if (sticky && mainButton && 'IntersectionObserver' in window) {
          this.observer?.disconnect();
          this.observer = new IntersectionObserver(([entry]) => {
            this.pastMainButton = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            sticky.hidden = !this.pastMainButton || this.footerVisible;
          }, { rootMargin: '0px 0px 10000px 0px', threshold: 0 });
          this.observer.observe(mainButton);
          const footer = document.querySelector('.xj-footer');
          if (footer) {
            this.footerObserver?.disconnect();
            this.footerObserver = new IntersectionObserver(([entry]) => {
              this.footerVisible = entry.isIntersecting;
              sticky.hidden = !this.pastMainButton || this.footerVisible;
            });
            this.footerObserver.observe(footer);
          }
        }
        customElements.whenDefined('product-form').then(() => {
          if (!this.isConnected || signal.aborted) return;
          const productForm = this.querySelector('product-form');
          if (!productForm || productForm.dataset.xenjoyErrorsReady) return;
          // Keep Dawn's cart request, section rendering and live announcements;
          // ensure its network-failure path also has a visible inline message.
          const dispatchError = productForm.dispatchCartErrorEvent.bind(productForm);
          productForm.dispatchCartErrorEvent = (message, code) => {
            productForm.handleErrorMessage(code === 'SERVICE_UNAVAILABLE'
              ? this.dataset.networkError
              : message);
            const error = productForm.querySelector('.product-form__error-message-wrapper');
            if (error && error.getBoundingClientRect().top < 0) error.scrollIntoView({ block: 'center' });
            dispatchError(message, code);
          };
          productForm.dataset.xenjoyErrorsReady = 'true';
        });
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        this.footerObserver?.disconnect();
        this.buttonObserver?.disconnect();
      }
      selectMedia(id) {
        const media = [...this.querySelectorAll('[data-product-media]')];
        if (!media.some((item) => item.dataset.productMedia === String(id))) return;
        media.forEach((item) => {
          item.hidden = item.dataset.productMedia !== String(id);
          if (item.hidden) {
            item.querySelectorAll('video').forEach((video) => video.pause());
            item.querySelectorAll('iframe').forEach((frame) => {
              if (frame.src.includes('youtube.com')) frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), 'https://www.youtube.com');
              if (frame.src.includes('vimeo.com')) frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), 'https://player.vimeo.com');
            });
          }
        });
        this.querySelectorAll('[data-media-target]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.mediaTarget === String(id))));
      }
      selectVariant(id) {
        const variant = this.variants.find((item) => String(item.id) === String(id));
        if (!variant) return;
        const price = this.querySelector('[data-product-price]');
        if (price) price.textContent = variant.price;
        const stickyPrice = this.querySelector('[data-sticky-price]');
        if (stickyPrice) stickyPrice.textContent = variant.price;
        const compare = this.querySelector('[data-product-compare]');
        if (compare) { compare.textContent = variant.compare; compare.hidden = !variant.showCompare; }
        const productForm = this.querySelector('product-form');
        const button = productForm?.querySelector('[type=submit]');
        if (button) {
          button.disabled = !variant.available;
          button.removeAttribute('aria-disabled');
          const text = button.querySelector('span');
          if (text) { text.textContent = variant.available ? this.dataset.addLabel : this.dataset.soldOutLabel; text.classList.remove('hidden'); }
        }
        const stickyButton = this.querySelector('[data-sticky-submit]');
        if (stickyButton) {
          stickyButton.disabled = !variant.available;
          stickyButton.textContent = variant.available ? this.dataset.addLabel : this.dataset.soldOutLabel;
        }
        productForm?.handleErrorMessage?.();
        const quantity = this.querySelector('[name=quantity]');
        if (quantity) {
          quantity.min = variant.minimum;
          quantity.step = variant.increment;
          if (variant.maximum !== null) quantity.max = variant.maximum;
          else quantity.removeAttribute('max');
          quantity.value = variant.minimum;
        }
        if (variant.mediaId) this.selectMedia(variant.mediaId);
        const url = new URL(window.location.href);
        if (url.pathname.includes('/products/')) {
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      }
    });
  }
})();
